import type { MarketId } from '@morpho-org/blue-sdk'
import type { ApiInputToken, Rewards } from '@morpho-org/dapp-core'

import { safeParseNumber } from '@morpho-org/blue-sdk-viem'

import type { CollateralRewardEntry } from '@/components/common/CollateralYieldTags'
import type { MarketApy } from '@/lib/procedures/marketApy.contract'
import type { MarketApyAverages } from '@/lib/procedures/marketApyAverages.contract'
import type { TokenRootOutput } from '@/lib/procedures/tokenRoot.contract'
import type { VaultV1Root } from '@/lib/procedures/vaultV1Root.contract'

import { getParsedValue } from '@/helpers/formatters'
import { normalizeMarketApyRewards, toCollateralRewardEntries } from '@/helpers/rewards'

import type { MarketApyRateMode } from './ApyCardMarketRateMode.context'

/**
 * View-model for the market APY card: the asset metadata from the `market.root`, `token.root` and
 * `vaultV1.root` procedures, the spot APYs and rewards (each with its Merkl campaign URL) from
 * `market.apy`, and the 6h/24h/7d averages from `market.apyAverages`. `averages` is null when that
 * procedure failed: the card still renders, without the 6h headline and the windows.
 */
export interface MarketApyCardData {
  chainId: number
  marketId: MarketId
  collateralAsset: ApiInputToken | null
  loanAsset: ApiInputToken
  apy: MarketApy
  averages: MarketApyAverages | null
}

export interface MarketApyRates {
  /** 6h-average native APY, rewards excluded, in WAD. */
  apy: bigint | null
  /** Spot native APY, rewards excluded, in WAD. */
  instantApy: bigint | null
  /** 6h-average native APY with the side's spot rewards applied once, in WAD. */
  netApy: bigint | null
  dailyApy: bigint | null
  weeklyApy: bigint | null
  rewards: Rewards[]
  /** Borrow side only: holding the collateral earns a borrower, not a lender. */
  collateralRewards: CollateralRewardEntry[] | null
}

/** The rates and rewards one side of the card shows. */
export const selectMarketApyRates = (
  market: Pick<MarketApyCardData, 'apy' | 'averages'>,
  rateMode: MarketApyRateMode
): MarketApyRates => {
  const side = market.apy[rateMode]
  const averages = market.averages?.[rateMode]
  const apy = getParsedValue(averages?.sixHourApy, safeParseNumber) ?? null
  const instantApy = getParsedValue(side.apy, safeParseNumber) ?? null
  const instantNetApy = getParsedValue(side.netApy, safeParseNumber) ?? null
  return {
    apy,
    instantApy,
    netApy:
      apy == null || instantApy == null || instantNetApy == null
        ? null
        : apy + instantNetApy - instantApy,
    dailyApy: getParsedValue(averages?.dailyApy, safeParseNumber) ?? null,
    weeklyApy: getParsedValue(averages?.weeklyApy, safeParseNumber) ?? null,
    rewards: normalizeMarketApyRewards(side.rewards, rateMode),
    collateralRewards:
      rateMode === 'borrow' ? toCollateralRewardEntries(market.apy.collateral.rewards) : null
  }
}

/** A `token.root` output in the shape `parseApiAsset` reads. */
export const toCardAsset = (
  token: TokenRootOutput,
  extra: Pick<ApiInputToken, 'yield' | 'vault'> = {}
): ApiInputToken => ({
  address: token.address,
  symbol: token.symbol ?? '',
  name: token.name ?? '',
  decimals: token.decimals,
  logoURI: token.logoUri,
  chain: { id: token.chainId },
  priceUsd: token.priceUsd,
  ...extra
})

/**
 * The collateral's own yield APY and, for a Vault V1 share, the vault the native-yield row links to.
 * A vault share's yield is the bridge's token yield, the same number the row shows for any other
 * yield-bearing token.
 */
export const toCollateralYield = (
  collateral: MarketApy['collateral'],
  vault: Pick<VaultV1Root, 'address' | 'name' | 'asset'> | null
): Pick<ApiInputToken, 'yield' | 'vault'> => {
  const apy = collateral.yield?.apy ?? null
  return {
    yield: apy == null ? null : { apy },
    vault: vault
      ? {
          address: vault.address,
          name: vault.name,
          asset: { logoURI: vault.asset.logoUri },
          state: { avgNetApy: apy }
        }
      : null
  }
}
